import React, { useState, useEffect } from 'react';
import './Actas.css';
import { useTemporada } from '../context/TemporadaContext';
import { getCalendarioDetalle } from '../services/jornadasService';
import {
  corregirPartido, corregirAlineacion, proponerResultado, confirmarPartido,
  registrarIncidencia, aplazarPartido, anularPartido,
} from '../services/partidosService';
import { getParticipantesReal } from '../services/participantesService';
import { nombreVisible } from '../utils/jugadores';
import ModalAplazar from './ModalAplazar';
import { useAuth } from '../context/AuthContext';

const MODALIDADES = [
  'DOBLES FEMENINO', 'DOBLES MASCULINO', 'DOBLES MIXTO',
  'INDIVIDUAL FEMENINO', 'INDIVIDUAL MASCULINO',
];

const NUM_SETS = 3;

// Margen para corregir un partido confirmado (igual que en la API): pasado,
// solo un ADMIN puede editarlo.
const DIAS_EDICION_CONFIRMADO = 3;
const edicionCerrada = (partido, esAdmin) => (
  !esAdmin && partido.estado === 'CONFIRMADO' && !!partido.confirmado_en
  && Date.now() - new Date(partido.confirmado_en).getTime() > DIAS_EDICION_CONFIRMADO * 24 * 60 * 60 * 1000
);

const capitalize = (s) => s.charAt(0) + s.slice(1).toLowerCase();

// Cuántas jugadoras hay en pista a la vez: 1 en individual, 2 en dobles.
// No lo da /calendario-detalle (solo el nombre de la modalidad), pero es
// una regla fija del reglamento (ver modalidades.jugadores_en_pista).
const jugadoresEnPista = (modalidad) => (modalidad.startsWith('INDIVIDUAL') ? 1 : 2);

// Puntos de los 3 huecos del formulario -> sets que existen de verdad
// (numero 1..3, solo los que tienen los dos lados rellenos), en el formato
// que espera PATCH /partidos/:id.
const setsRellenosA = (huecos) => huecos
  .map((h, i) => ({ numero: i + 1, puntosLocal: h.puntosLocal, puntosVisitante: h.puntosVisitante }))
  .filter((s) => s.puntosLocal !== '' && s.puntosVisitante !== '')
  .map((s) => ({ ...s, puntosLocal: Number(s.puntosLocal), puntosVisitante: Number(s.puntosVisitante) }));

// El separador apunta del lado con más puntos al de menos (5 < 21, 21 > 5),
// no siempre hacia la derecha. Sin puntos en ningún lado (set aún no jugado),
// ni "<" ni ">" tienen sentido: se muestra un guion neutro. A puntos iguales
// (mientras se escribe; un set no debería terminar en empate), "=".
const separadorSet = (puntosLocal, puntosVisitante) => {
  if (puntosLocal === '' && puntosVisitante === '') return '-';
  const local = Number(puntosLocal);
  const visitante = Number(puntosVisitante);
  if (local === visitante) return '=';
  return local < visitante ? '<' : '>';
};

// En bádminton un set hay que ganarlo por al menos 2 puntos (22-20 es un
// final válido, 22-21 no lo es): diferencia menor de 2 -> posible resultado
// incompleto o error de tecleo, se marca con un asterisco rojo junto al
// cuadro del segundo lado.
const esDiferenciaAjustada = (puntosLocal, puntosVisitante) => (
  puntosLocal !== '' && puntosVisitante !== ''
    && Math.abs(Number(puntosLocal) - Number(puntosVisitante)) < 2
);

// Mismo cálculo que v_partidos_resumen (sets ganados por lado, contando
// puntos): así el marcador de cabecera se recalcula solo mientras se edita,
// sin esperar a guardar ni a que el servidor lo confirme.
const calcularMarcador = (sets) => sets.reduce(
  (acc, s) => {
    if (s.puntosLocal > s.puntosVisitante) acc.local += 1;
    else if (s.puntosVisitante > s.puntosLocal) acc.visitante += 1;
    return acc;
  },
  { local: 0, visitante: 0 },
);

const huecosIniciales = (partido) => Array.from({ length: NUM_SETS }, (_, i) => {
  const set = partido.sets.find((s) => s.numero === i + 1);
  return { puntosLocal: set ? String(set.puntosLocal) : '', puntosVisitante: set ? String(set.puntosVisitante) : '' };
});

// Un hueco de selector por jugadora en pista, relleno con quien ya está
// alineado (si hay). El orden de partido.alineacion_* no tiene un "puesto"
// propio en el modelo (solo lado), así que solo importa que cada jugadora
// aparezca en algún hueco, no en cuál.
const alineacionInicial = (partido) => {
  const enPista = jugadoresEnPista(partido.modalidad);
  const rellenar = (jugadores) => Array.from({ length: enPista }, (_, i) => (
    jugadores[i] ? String(jugadores[i].jugadorId) : ''
  ));
  return { local: rellenar(partido.alineacion_local), visitante: rellenar(partido.alineacion_visitante) };
};

// Si un lado no tiene a nadie alineado y su plantilla tiene exactamente tantos
// jugadores como huecos (p. ej. 2 en dobles), no hay nada que elegir: se
// rellenan solos. Con más jugadores (3) se dejan vacíos para el comité.
const alineacionConAutorrelleno = (partido, rosterPorId) => {
  const inicial = alineacionInicial(partido);
  const rellenarLado = (lado, participanteId) => {
    const plantilla = rosterPorId[participanteId] ?? [];
    if (inicial[lado].some(Boolean) || plantilla.length !== inicial[lado].length) return inicial[lado];
    return plantilla.map((j) => String(j.jugadorId));
  };
  return {
    local: rellenarLado('local', partido.local_participante_id),
    visitante: rellenarLado('visitante', partido.visitante_participante_id),
  };
};

const ETIQUETAS_ESTADO = {
  PENDIENTE: 'Pendiente', PROPUESTO: 'Propuesto', CONFIRMADO: 'Confirmado',
  APLAZADO: 'Aplazado', ANULADO: 'Anulado',
};

// El orden es el del desplegable. WO_INCOMPARECENCIA es la incomparecencia
// CON aviso en plazo (0 puntos); WO_FUERA_DE_PLAZO, avisando con menos de 7 días
// (-1); SIN_AVISO es la penalizada (-3). El rival suma siempre la victoria.
const ETIQUETAS_INCIDENCIA = {
  WO_INCOMPARECENCIA: 'Incomparecencia (con aviso en plazo)',
  WO_FUERA_DE_PLAZO: 'Incomparecencia (aviso fuera de plazo)',
  SIN_AVISO: 'Incomparecencia sin aviso (penalización)',
  WO_LESION: 'Lesión',
  WO_SANCION: 'Sanción',
  NO_DISPUTADO: 'No disputado',
  INTERRUMPIDO: 'Interrumpido',
};

const INCIDENCIA_INICIAL = {
  abierta: false, tipo: '', ganador: '', setsLocal: '', setsVisitante: '', notas: '',
};

// Confirmar (POST /partidos/:id/confirmar) solo funciona desde PROPUESTO.
// Desde PENDIENTE hace falta pasar antes por proponer resultado — por eso
// solo se ofrece el botón en estos dos estados, no en APLAZADO/ANULADO.
// Puntos de ranking del baremo (clasif_*, ya incluye las penalizaciones de
// incidencias) que suma cada lado; solo cuando el partido está confirmado.
const PuntosClasif = ({ partido, lado }) => {
  const n = partido.estado === 'CONFIRMADO' ? partido[`clasif_${lado}`] : null;
  if (n == null) return null;
  const v = Number(n);
  return (
    <span className={`acta-puntos-clasif${v < 0 ? ' negativo' : ''}`}>
      {lado === 'local' ? ' ' : ''}({v > 0 ? '+' : ''}{v}){lado === 'visitante' ? ' ' : ''}
    </span>
  );
};

const puedeConfirmar = (estado) => estado === 'PENDIENTE' || estado === 'PROPUESTO';

// Desde PENDIENTE, marcar una incidencia usa /resultado (igual que
// proponer un marcador normal); desde cualquier otro estado no terminal
// (error de clasificación ya PROPUESTO o CONFIRMADO) usa PATCH /partidos/:id
// en su lugar — ver guardarIncidencia. Un ANULADO no admite ninguno de
// los dos caminos.
const puedeMarcarIncidencia = (estado) => estado !== 'ANULADO';

// El backend solo rechaza aplazar un CONFIRMADO o ANULADO, pero no tiene
// sentido ofrecer el botón sobre un partido que ya está APLAZADO.
const puedeAplazar = (estado) => estado !== 'CONFIRMADO' && estado !== 'ANULADO' && estado !== 'APLAZADO';

// Descalificación del equipo: mismas restricciones que aplazar (nada
// CONFIRMADO ni ya ANULADO), pero sin excluir APLAZADO — un equipo puede
// descalificarse después de haber pedido un aplazamiento.
const puedeAnular = (estado) => estado !== 'CONFIRMADO' && estado !== 'ANULADO';

const mensajeError = (err) => (
  err?.codigo === 'SIN_PERMISO'
    ? 'No tienes permiso para corregir este partido.'
    : err?.codigo === 'NO_AUTENTICADO'
      ? 'Tienes que iniciar sesión para corregir un resultado.'
      : err?.message || 'Error al guardar.'
);

function Actas() {
  const { temporada } = useTemporada();
  const { usuario } = useAuth();
  const esAdmin = usuario?.rol === 'ADMIN';
  const [partidos, setPartidos] = useState([]);
  const [roster, setRoster] = useState({}); // { [participanteId]: [{jugadorId, nombre, genero}] }
  const [sets, setSets] = useState({}); // { [partidoId]: [{puntosLocal, puntosVisitante}, ...] }
  const [alineacion, setAlineacion] = useState({}); // { [partidoId]: { local: [jugadorId,...], visitante: [...] } }
  const [guardado, setGuardado] = useState({}); // { [partidoId]: { guardando, feedback } }
  const [incidencias, setIncidencias] = useState({}); // { [partidoId]: { abierta, tipo, ganador, setsLocal, setsVisitante, notas } }
  const [forzarNormal, setForzarNormal] = useState({}); // { [partidoId]: true } — ver "Convertir en resultado normal"
  const [aplazando, setAplazando] = useState(null); // partido que se está aplazando (abre ModalAplazar)
  const [abiertas, setAbiertas] = useState({}); // { [modalidad]: true }
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [filtroJornada, setFiltroJornada] = useState('');
  const [filtroEstado, setFiltroEstado] = useState(''); // '' = todos | 'COMPLETADOS' | 'PENDIENTES'

  useEffect(() => {
    if (!temporada) return;
    setCargando(true);
    setError(null);
    Promise.all([getCalendarioDetalle(temporada), getParticipantesReal(temporada)])
      .then(([partidosDatos, participantesDatos]) => {
        setPartidos(partidosDatos);
        setRoster(Object.fromEntries(participantesDatos.map((p) => [p.participante_id, p.jugadores])));
        setSets(Object.fromEntries(partidosDatos.map((p) => [p.id, huecosIniciales(p)])));
        const rosterPorId = Object.fromEntries(participantesDatos.map((p) => [p.participante_id, p.jugadores]));
        setAlineacion(Object.fromEntries(partidosDatos.map((p) => [
          p.id,
          alineacionConAutorrelleno(p, rosterPorId),
        ])));
        setGuardado({});
        setIncidencias({});
        setForzarNormal({});
      })
      .catch(() => setError('No se pudieron cargar las actas. ¿Está el servidor en marcha?'))
      .finally(() => setCargando(false));
  }, [temporada]);

  const cambiarFiltroEstado = (valor) => {
    setFiltroEstado(valor);
    if (valor) setAbiertas(Object.fromEntries(MODALIDADES.map((mod) => [mod, true])));
  };

  const toggleModalidad = (mod) => setAbiertas((prev) => ({ ...prev, [mod]: !prev[mod] }));

  const cambiarSet = (partidoId, index, esLocal, valor) => {
    setSets((prev) => ({
      ...prev,
      [partidoId]: prev[partidoId].map((h, i) => (
        i === index ? { ...h, [esLocal ? 'puntosLocal' : 'puntosVisitante']: valor } : h
      )),
    }));
  };

  const cambiarAlineacion = (partidoId, lado, index, valor) => {
    setAlineacion((prev) => ({
      ...prev,
      [partidoId]: {
        ...prev[partidoId],
        [lado]: prev[partidoId][lado].map((v, i) => (i === index ? valor : v)),
      },
    }));
  };

  const guardarPartido = async (partido) => {
    const huecos = setsRellenosA(sets[partido.id]);
    const seleccion = alineacion[partido.id];
    const inicial = alineacionInicial(partido);
    const cambioLocal = seleccion.local.some(Boolean)
      && JSON.stringify(seleccion.local) !== JSON.stringify(inicial.local);
    const cambioVisitante = seleccion.visitante.some(Boolean)
      && JSON.stringify(seleccion.visitante) !== JSON.stringify(inicial.visitante);

    if (huecos.length === 0 && !cambioLocal && !cambioVisitante) {
      setGuardado((prev) => ({ ...prev, [partido.id]: { guardando: false, feedback: { tipo: 'error', msg: 'No hay ningún cambio que guardar.' } } }));
      return;
    }

    setGuardado((prev) => ({ ...prev, [partido.id]: { guardando: true, feedback: null } }));

    const idsA = (seleccionLado) => seleccionLado.filter(Boolean).map((id) => ({ jugadorId: Number(id), sustitucion: false }));

    const tareas = [];
    if (huecos.length > 0) tareas.push(corregirPartido(partido.id, { sets: huecos }));
    if (cambioLocal) tareas.push(corregirAlineacion(partido.id, { lado: 'LOCAL', jugadores: idsA(seleccion.local) }));
    if (cambioVisitante) tareas.push(corregirAlineacion(partido.id, { lado: 'VISITANTE', jugadores: idsA(seleccion.visitante) }));

    const resultados = await Promise.allSettled(tareas);
    const fallos = resultados.filter((r) => r.status === 'rejected');

    if (fallos.length > 0) {
      const msg = fallos.map((f) => mensajeError(f.reason)).join(' / ');
      setGuardado((prev) => ({ ...prev, [partido.id]: { guardando: false, feedback: { tipo: 'error', msg } } }));
      return;
    }

    setGuardado((prev) => ({ ...prev, [partido.id]: { guardando: false, feedback: { tipo: 'ok', msg: 'Guardado.' } } }));
    // Mandar sets convierte cualquier walkover previo en resultado normal
    // (el backend limpia incidencia/marcador manual a la vez); si venía de
    // "Convertir en resultado normal", ya no hace falta forzar la vista.
    if (huecos.length > 0) setForzarNormal((prev) => ({ ...prev, [partido.id]: false }));
    setPartidos((prev) => prev.map((p) => {
      if (p.id !== partido.id) return p;
      const nombreDe = (participanteId, jugadorId) => roster[participanteId]?.find((j) => String(j.jugadorId) === jugadorId)?.nombre ?? '';
      const aliasDe = (participanteId, jugadorId) => roster[participanteId]?.find((j) => String(j.jugadorId) === jugadorId)?.alias ?? null;
      return {
        ...p,
        sets: huecos.length > 0 ? huecos : p.sets,
        incidencia: huecos.length > 0 ? null : p.incidencia,
        alineacion_local: cambioLocal
          ? seleccion.local.filter(Boolean).map((id) => ({ jugadorId: Number(id), nombre: nombreDe(p.local_participante_id, id), alias: aliasDe(p.local_participante_id, id) }))
          : p.alineacion_local,
        alineacion_visitante: cambioVisitante
          ? seleccion.visitante.filter(Boolean).map((id) => ({ jugadorId: Number(id), nombre: nombreDe(p.visitante_participante_id, id), alias: aliasDe(p.visitante_participante_id, id) }))
          : p.alineacion_visitante,
      };
    }));
  };

  const confirmarResultado = async (partido) => {
    setGuardado((prev) => ({ ...prev, [partido.id]: { ...prev[partido.id], confirmando: true, feedback: null } }));
    try {
      if (partido.estado === 'PENDIENTE') {
        if (!partido.sets.length) {
          throw new Error('Guarda el resultado antes de confirmar.');
        }
        await proponerResultado(partido.id, partido.sets);
      }
      await confirmarPartido(partido.id);
      setGuardado((prev) => ({ ...prev, [partido.id]: { guardando: false, confirmando: false, feedback: { tipo: 'ok', msg: 'Confirmado.' } } }));
      setPartidos((prev) => prev.map((p) => (p.id === partido.id ? { ...p, estado: 'CONFIRMADO' } : p)));
    } catch (err) {
      setGuardado((prev) => ({ ...prev, [partido.id]: { ...prev[partido.id], confirmando: false, feedback: { tipo: 'error', msg: mensajeError(err) } } }));
    }
  };

  const toggleIncidencia = (partidoId) => setIncidencias((prev) => ({
    ...prev,
    [partidoId]: { ...INCIDENCIA_INICIAL, ...prev[partidoId], abierta: !prev[partidoId]?.abierta },
  }));

  const cambiarIncidencia = (partidoId, campo, valor) => setIncidencias((prev) => ({
    ...prev,
    [partidoId]: { ...INCIDENCIA_INICIAL, ...prev[partidoId], [campo]: valor },
  }));

  const guardarIncidencia = async (partido) => {
    const datos = incidencias[partido.id] ?? INCIDENCIA_INICIAL;
    if (!datos.tipo || !datos.ganador || datos.setsLocal === '' || datos.setsVisitante === '') {
      setGuardado((prev) => ({ ...prev, [partido.id]: { guardando: false, feedback: { tipo: 'error', msg: 'Completa el tipo de incidencia, el ganador y el marcador manual.' } } }));
      return;
    }

    setGuardado((prev) => ({ ...prev, [partido.id]: { guardando: true, feedback: null } }));
    // Desde PENDIENTE no hay nada que corregir todavía: /resultado (misma
    // llamada que un marcador normal). Desde PROPUESTO/CONFIRMADO/APLAZADO
    // ya hay algo que sustituir, así que va por PATCH /partidos/:id — ese
    // sí sabe convertir un resultado normal en walkover sin tocar el estado.
    const esPendiente = partido.estado === 'PENDIENTE';
    const payload = {
      incidencia: datos.tipo,
      setsManualLocal: Number(datos.setsLocal),
      setsManualVisitante: Number(datos.setsVisitante),
      ganadorParticipanteId: Number(datos.ganador),
      ...(datos.notas ? { notas: datos.notas } : {}),
    };
    try {
      if (esPendiente) await registrarIncidencia(partido.id, payload);
      else await corregirPartido(partido.id, payload);
      setGuardado((prev) => ({ ...prev, [partido.id]: { guardando: false, feedback: { tipo: 'ok', msg: esPendiente ? 'Incidencia guardada. Pulsa Confirmar para cerrar el partido.' : 'Incidencia guardada.' } } }));
      setIncidencias((prev) => ({ ...prev, [partido.id]: { ...prev[partido.id], abierta: false } }));
      setPartidos((prev) => prev.map((p) => (p.id === partido.id ? {
        ...p,
        estado: esPendiente ? 'PROPUESTO' : p.estado,
        incidencia: datos.tipo,
        sets: [],
        sets_local: Number(datos.setsLocal),
        sets_visitante: Number(datos.setsVisitante),
      } : p)));
    } catch (err) {
      setGuardado((prev) => ({ ...prev, [partido.id]: { guardando: false, feedback: { tipo: 'error', msg: mensajeError(err) } } }));
    }
  };

  // Aplazar pide motivo, quién aplaza y fecha de comunicación: se hace en un
  // diálogo (ModalAplazar), que llama aquí al confirmar. Si falla, el error
  // se muestra dentro del diálogo y el partido no cambia.
  const confirmarAplazamiento = async (datos) => {
    const partido = aplazando;
    await aplazarPartido(partido.id, datos);
    setGuardado((prev) => ({ ...prev, [partido.id]: { ...prev[partido.id], feedback: { tipo: 'ok', msg: 'Aplazado.' } } }));
    setPartidos((prev) => prev.map((p) => (p.id === partido.id ? { ...p, estado: 'APLAZADO' } : p)));
    setAplazando(null);
  };

  // Fecha real en que se jugó (arranca el plazo de comunicar puntos). Se guarda
  // al cambiarla; vacía la borra.
  const guardarFechaJugado = async (partido, valor) => {
    setGuardado((prev) => ({ ...prev, [partido.id]: { ...prev[partido.id], feedback: null } }));
    try {
      await corregirPartido(partido.id, { fechaJugado: valor || null });
      setPartidos((prev) => prev.map((p) => (p.id === partido.id ? { ...p, fecha_jugado: valor || null } : p)));
      setGuardado((prev) => ({ ...prev, [partido.id]: { ...prev[partido.id], feedback: { tipo: 'ok', msg: 'Fecha guardada.' } } }));
    } catch (err) {
      setGuardado((prev) => ({ ...prev, [partido.id]: { ...prev[partido.id], feedback: { tipo: 'error', msg: mensajeError(err) } } }));
    }
  };

  const anular = async (partido) => {
    const motivo = window.prompt('Motivo de la descalificación (equipo y por qué):');
    if (!motivo) return;

    setGuardado((prev) => ({ ...prev, [partido.id]: { ...prev[partido.id], anulando: true, feedback: null } }));
    try {
      await anularPartido(partido.id, motivo);
      setGuardado((prev) => ({ ...prev, [partido.id]: { ...prev[partido.id], anulando: false, feedback: { tipo: 'ok', msg: 'Partido anulado.' } } }));
      setPartidos((prev) => prev.map((p) => (p.id === partido.id ? { ...p, estado: 'ANULADO' } : p)));
    } catch (err) {
      setGuardado((prev) => ({ ...prev, [partido.id]: { ...prev[partido.id], anulando: false, feedback: { tipo: 'error', msg: mensajeError(err) } } }));
    }
  };

  // "Convertir en resultado normal" no toca el servidor por sí sola: solo
  // fuerza la vista editable de sets para un partido con incidencia, para
  // que se pueda teclear el marcador real y guardarlo con el botón
  // "Guardar" de siempre (que es quien de verdad limpia la incidencia).
  const toggleForzarNormal = (partidoId) => setForzarNormal((prev) => ({ ...prev, [partidoId]: !prev[partidoId] }));

  // Selector único de "cambiar estado" en la card: cada opción dispara la
  // acción correspondiente (no hay un valor que quede seleccionado, es un
  // menú de acciones, no un campo).
  const manejarCambioEstado = (partido, valor) => {
    if (valor === 'FINALIZADO') confirmarResultado(partido);
    else if (valor === 'APLAZADO') setAplazando(partido);
    else if (valor === 'DESCALIFICADO') anular(partido);
    else if (valor === 'INCOMPARECENCIA') {
      setIncidencias((prev) => ({
        ...prev,
        [partido.id]: { ...INCIDENCIA_INICIAL, ...prev[partido.id], abierta: true, tipo: 'WO_INCOMPARECENCIA' },
      }));
    }
  };

  const jornadasDisponibles = Array.from(new Set(partidos.map((p) => p.jornada))).sort((a, b) => a - b);
  // Mismo criterio que el resumen de cada jornada: completado = CONFIRMADO o ANULADO.
  const estaCompletado = (p) => p.estado === 'CONFIRMADO' || p.estado === 'ANULADO';
  const deLaJornada = filtroJornada
    ? partidos.filter((p) => p.jornada === Number(filtroJornada))
    : partidos;
  const partidosFiltrados = filtroEstado
    ? deLaJornada.filter((p) => (filtroEstado === 'COMPLETADOS') === estaCompletado(p))
    : deLaJornada;

  const modalidadesVisibles = MODALIDADES.filter((mod) => partidosFiltrados.some((p) => p.modalidad === mod));
  const todasAbiertas = modalidadesVisibles.length > 0 && modalidadesVisibles.every((mod) => abiertas[mod]);
  const alternarTodas = () => setAbiertas(Object.fromEntries(MODALIDADES.map((mod) => [mod, !todasAbiertas])));

  return (
    <div className="actas-page">
      <div className="actas-cabecera">
        <h2 className="actas-titulo">Actas · Temporada {temporada}</h2>

        <div className="actas-controles">
          {partidos.length > 0 && (
            <span className="actas-contador">{partidosFiltrados.length} de {deLaJornada.length} partidos</span>
          )}

          {jornadasDisponibles.length > 0 && (
            <select
              className="actas-filtro-jornada"
              value={filtroJornada}
              onChange={(e) => setFiltroJornada(e.target.value)}
            >
              <option value="">Todas las jornadas</option>
              {jornadasDisponibles.map((num) => (
                <option key={num} value={num}>Jornada {num}</option>
              ))}
            </select>
          )}

          <select
            className="actas-filtro-jornada"
            value={filtroEstado}
            onChange={(e) => cambiarFiltroEstado(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="COMPLETADOS">Completados</option>
            <option value="PENDIENTES">Pendientes</option>
          </select>

          <button type="button" className="actas-plegar-btn" onClick={alternarTodas} disabled={modalidadesVisibles.length === 0}>
            {todasAbiertas ? 'Plegar todo' : 'Desplegar todo'}
          </button>
        </div>
      </div>

      {cargando && <p className="actas-mensaje">Cargando...</p>}
      {error && <p className="actas-mensaje actas-error">{error}</p>}
      {!cargando && !error && partidos.length === 0 && (
        <p className="actas-mensaje">No hay partidos para esta temporada.</p>
      )}

      {MODALIDADES.map((mod) => {
        const deLaModalidad = partidosFiltrados.filter((p) => p.modalidad === mod);
        const totalModalidad = deLaJornada.filter((p) => p.modalidad === mod).length;
        if (!cargando && !error && deLaModalidad.length === 0) return null;

        const porJornada = deLaModalidad.reduce((acc, p) => {
          (acc[p.jornada] ??= []).push(p);
          return acc;
        }, {});
        const jornadas = Object.keys(porJornada).map(Number).sort((a, b) => a - b);
        const isOpen = !!abiertas[mod];
        const enPista = jugadoresEnPista(mod);

        return (
          <div key={mod} className="actas-modalidad">
            <button className="actas-modalidad-btn" onClick={() => toggleModalidad(mod)}>
              {capitalize(mod)} ({deLaModalidad.length} de {totalModalidad})
            </button>

            {isOpen && jornadas.map((num) => {
              const pendientes = porJornada[num].filter((p) => p.estado !== 'CONFIRMADO' && p.estado !== 'ANULADO').length;
              return (
              <div key={num} className="actas-jornada">
                <h4>
                  Jornada {num}
                  <span className={`actas-jornada-resumen ${pendientes === 0 ? 'completada' : 'pendiente'}`}>
                    {pendientes === 0 ? 'Completado' : `${pendientes} pendiente${pendientes === 1 ? '' : 's'}`}
                  </span>
                </h4>
                {porJornada[num].map((partido) => {
                  const huecos = sets[partido.id] ?? huecosIniciales(partido);
                  const seleccion = alineacion[partido.id] ?? alineacionInicial(partido);
                  const rosterLocal = roster[partido.local_participante_id] ?? [];
                  const rosterVisitante = roster[partido.visitante_participante_id] ?? [];
                  // "Convertir en resultado normal" (forzarNormal) fuerza esta
                  // vista aunque el partido siga teniendo incidencia en el
                  // servidor hasta que se pulse Guardar. Un ANULADO nunca es
                  // editable, tenga o no incidencia.
                  const editable = (!partido.incidencia || forzarNormal[partido.id]) && partido.estado !== 'ANULADO'
                    && !edicionCerrada(partido, esAdmin);
                  const marcador = editable
                    ? calcularMarcador(setsRellenosA(huecos))
                    : { local: partido.sets_local, visitante: partido.sets_visitante };
                  const estado = guardado[partido.id];

                  return (
                    <div key={partido.id} className="acta">
                      <div className="acta-cabecera">
                        <span className="acta-equipo acta-equipo-local">
                          {partido.local_nombre}
                          <PuntosClasif partido={partido} lado="local" />
                        </span>
                        <span className="acta-marcador">
                          {marcador.local != null ? `${marcador.local} - ${marcador.visitante}` : 'vs'}
                        </span>
                        <span className="acta-equipo acta-equipo-visitante">
                          <PuntosClasif partido={partido} lado="visitante" />
                          {partido.visitante_nombre}
                        </span>
                      </div>

                      <span className={`acta-estado acta-estado-${partido.estado.toLowerCase()}`}>
                        {ETIQUETAS_ESTADO[partido.estado] ?? partido.estado}
                      </span>
                      <span className="acta-id">#{partido.id}</span>

                      {partido.estado !== 'ANULADO' && partido.estado !== 'APLAZADO' && (
                        <label className="acta-fecha-jugado">
                          Fecha jugado{' '}
                          <input
                            type="date"
                            aria-label={`Fecha jugado del partido ${partido.id}`}
                            value={partido.fecha_jugado ?? ''}
                            disabled={edicionCerrada(partido, esAdmin)}
                            onChange={(e) => guardarFechaJugado(partido, e.target.value)}
                          />
                        </label>
                      )}

                      {!editable && partido.incidencia && (
                        <p className="acta-incidencia">
                          Incidencia: {ETIQUETAS_INCIDENCIA[partido.incidencia] ?? partido.incidencia}
                          {!edicionCerrada(partido, esAdmin) && (
                            <>
                              {' '}— si fue un error, puedes convertirlo en un resultado normal.{' '}
                              <button
                                type="button"
                                className="acta-incidencia-toggle"
                                onClick={() => toggleForzarNormal(partido.id)}
                              >
                                Convertir en resultado normal
                              </button>
                            </>
                          )}
                        </p>
                      )}

                      {partido.estado === 'ANULADO' && (
                        <p className="acta-incidencia">
                          Partido anulado{partido.notas ? `: ${partido.notas}` : '.'}
                        </p>
                      )}

                      {editable && puedeMarcarIncidencia(partido.estado) && incidencias[partido.id]?.abierta && (
                        <div className="acta-incidencia-zona">
                          <button
                            type="button"
                            className="acta-incidencia-toggle"
                            onClick={() => toggleIncidencia(partido.id)}
                          >
                            Cancelar incidencia
                          </button>

                          {incidencias[partido.id]?.abierta && (
                            <div className="acta-incidencia-form">
                              <select
                                className="acta-incidencia-select"
                                value={incidencias[partido.id]?.tipo ?? ''}
                                onChange={(e) => cambiarIncidencia(partido.id, 'tipo', e.target.value)}
                              >
                                <option value="">— tipo de incidencia —</option>
                                {Object.entries(ETIQUETAS_INCIDENCIA).map(([valor, etiqueta]) => (
                                  <option key={valor} value={valor}>{etiqueta}</option>
                                ))}
                              </select>

                              <div className="acta-incidencia-ganador">
                                <label>
                                  <input
                                    type="radio"
                                    name={`ganador-${partido.id}`}
                                    checked={incidencias[partido.id]?.ganador === String(partido.local_participante_id)}
                                    onChange={() => cambiarIncidencia(partido.id, 'ganador', String(partido.local_participante_id))}
                                  />
                                  {partido.local_nombre}
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name={`ganador-${partido.id}`}
                                    checked={incidencias[partido.id]?.ganador === String(partido.visitante_participante_id)}
                                    onChange={() => cambiarIncidencia(partido.id, 'ganador', String(partido.visitante_participante_id))}
                                  />
                                  {partido.visitante_nombre}
                                </label>
                              </div>

                              <div className="acta-incidencia-marcador">
                                <input
                                  type="number" min={0} className="acta-set-input"
                                  value={incidencias[partido.id]?.setsLocal ?? ''}
                                  onChange={(e) => cambiarIncidencia(partido.id, 'setsLocal', e.target.value)}
                                />
                                <span className="acta-set-separador">-</span>
                                <input
                                  type="number" min={0} className="acta-set-input"
                                  value={incidencias[partido.id]?.setsVisitante ?? ''}
                                  onChange={(e) => cambiarIncidencia(partido.id, 'setsVisitante', e.target.value)}
                                />
                              </div>

                              <input
                                type="text"
                                className="acta-incidencia-notas"
                                placeholder="Notas (opcional)"
                                value={incidencias[partido.id]?.notas ?? ''}
                                onChange={(e) => cambiarIncidencia(partido.id, 'notas', e.target.value)}
                              />

                              <button
                                type="button"
                                className="acta-guardar-btn"
                                onClick={() => guardarIncidencia(partido)}
                                disabled={estado?.guardando}
                              >
                                {estado?.guardando ? 'Guardando...' : 'Guardar incidencia'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {Array.from({ length: NUM_SETS }, (_, i) => {
                        const hueco = huecos[i];

                        return (
                          <div key={i} className="acta-fila">
                            <span className="acta-jugadora acta-jugadora-local">
                              {editable && i < enPista && (
                                <>
                                  <span className="acta-jugadora-dash">-</span>
                                  <select
                                    className="acta-jugadora-select"
                                    value={seleccion.local[i]}
                                    onChange={(e) => cambiarAlineacion(partido.id, 'local', i, e.target.value)}
                                  >
                                    <option value="">— sin asignar —</option>
                                    {rosterLocal.map((j) => (
                                      <option key={j.jugadorId} value={j.jugadorId}>{nombreVisible(j)}</option>
                                    ))}
                                  </select>
                                </>
                              )}
                              {!editable && partido.alineacion_local[i] && `- ${nombreVisible(partido.alineacion_local[i])}`}
                            </span>

                            {editable && hueco ? (
                              <span className="acta-set-editable">
                                <input
                                  type="number" min={0} className="acta-set-input"
                                  value={hueco.puntosLocal}
                                  onChange={(e) => cambiarSet(partido.id, i, true, e.target.value)}
                                />
                                <span className="acta-set-separador">
                                  {separadorSet(hueco.puntosLocal, hueco.puntosVisitante)}
                                </span>
                                <input
                                  type="number" min={0} className="acta-set-input"
                                  value={hueco.puntosVisitante}
                                  onChange={(e) => cambiarSet(partido.id, i, false, e.target.value)}
                                />
                                {esDiferenciaAjustada(hueco.puntosLocal, hueco.puntosVisitante) && (
                                  <span className="acta-set-ajustado">*</span>
                                )}
                              </span>
                            ) : (
                              <span className="acta-set">
                                {partido.sets[i] && (
                                  <>
                                    {partido.sets[i].puntosLocal}
                                    <span className="acta-set-separador">
                                      {separadorSet(partido.sets[i].puntosLocal, partido.sets[i].puntosVisitante)}
                                    </span>
                                    {partido.sets[i].puntosVisitante}
                                    {esDiferenciaAjustada(partido.sets[i].puntosLocal, partido.sets[i].puntosVisitante) && (
                                      <span className="acta-set-ajustado">*</span>
                                    )}
                                  </>
                                )}
                              </span>
                            )}

                            <span className="acta-jugadora acta-jugadora-visitante">
                              {editable && i < enPista && (
                                <>
                                  <span className="acta-jugadora-dash">-</span>
                                  <select
                                    className="acta-jugadora-select"
                                    value={seleccion.visitante[i]}
                                    onChange={(e) => cambiarAlineacion(partido.id, 'visitante', i, e.target.value)}
                                  >
                                    <option value="">— sin asignar —</option>
                                    {rosterVisitante.map((j) => (
                                      <option key={j.jugadorId} value={j.jugadorId}>{nombreVisible(j)}</option>
                                    ))}
                                  </select>
                                </>
                              )}
                              {!editable && partido.alineacion_visitante[i] && `- ${nombreVisible(partido.alineacion_visitante[i])}`}
                            </span>
                          </div>
                        );
                      })}

                      {(editable || puedeConfirmar(partido.estado) || puedeAplazar(partido.estado) || puedeAnular(partido.estado)) && (
                        <div className="acta-acciones">
                          {estado?.feedback && (
                            <span className={`acta-feedback acta-feedback-${estado.feedback.tipo}`}>
                              {estado.feedback.msg}
                            </span>
                          )}
                          {editable && (
                            <button
                              className="acta-guardar-btn"
                              onClick={() => guardarPartido(partido)}
                              disabled={estado?.guardando}
                            >
                              {estado?.guardando ? 'Guardando...' : 'Guardar'}
                            </button>
                          )}
                          {!edicionCerrada(partido, esAdmin) && (puedeConfirmar(partido.estado) || puedeAplazar(partido.estado)
                            || puedeMarcarIncidencia(partido.estado) || puedeAnular(partido.estado)) && (
                            <select
                              className="acta-estado-select"
                              value=""
                              onChange={(e) => manejarCambioEstado(partido, e.target.value)}
                              disabled={estado?.confirmando || estado?.anulando}
                            >
                              <option value="">Cambiar estado…</option>
                              {puedeConfirmar(partido.estado) && <option value="FINALIZADO">Finalizado</option>}
                              {puedeAplazar(partido.estado) && <option value="APLAZADO">Aplazado</option>}
                              {puedeMarcarIncidencia(partido.estado) && <option value="INCOMPARECENCIA">Incomparecencia</option>}
                              {puedeAnular(partido.estado) && <option value="DESCALIFICADO">Descalificado</option>}
                            </select>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              );
            })}
          </div>
        );
      })}

      {aplazando && (
        <ModalAplazar partido={aplazando} onConfirmar={confirmarAplazamiento} onCerrar={() => setAplazando(null)} />
      )}
    </div>
  );
}

export default Actas;
